import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Luggage, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Overline } from "@/components/Reveal";

function VehicleCard({ v, index }) {
  const images = useMemo(() => [v.image, ...(v.gallery || [])].filter(Boolean), [v]);
  const [active, setActive] = useState(0);

  return (
    <Reveal delay={index * 0.1}>
      <article
        data-testid={`fleet-vehicle-card-${v.id}`}
        className="group relative border border-[#2E3340] bg-[#1E2128] overflow-hidden hover:border-[#C5A059]/70 transition-colors duration-500"
      >
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            key={images[active]}
            src={images[active]}
            alt={v.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.4s] ease-out"
            data-testid={`fleet-vehicle-image-${v.id}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1E2128] via-transparent to-transparent" />
          <span className="absolute top-5 left-5 font-mono-lux text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 bg-black/50 backdrop-blur border border-amber-500/30 text-amber-300">
            {v.category}
          </span>
        </div>
        {images.length > 1 && (
          <div className="flex gap-2 px-7 lg:px-9 pt-4" data-testid={`fleet-gallery-${v.id}`}>
            {images.map((img, i) => (
              <button
                key={img}
                onClick={() => setActive(i)}
                data-testid={`fleet-gallery-thumb-${v.id}-${i}`}
                aria-label={`View photo ${i + 1} of ${v.name}`}
                className={`relative w-16 h-11 overflow-hidden border transition-colors duration-300 ${
                  active === i ? "border-amber-500" : "border-[#2E3340] opacity-50 hover:opacity-90"
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
        <div className="p-7 lg:p-9">
          <div className="flex items-start justify-between gap-4">
            <h3 className="font-serif-lux text-3xl text-amber-50">{v.name}</h3>
            <p className="text-right shrink-0">
              <span className="font-serif-lux text-2xl gold-text">${v.hourly_rate}</span>
              <span className="block font-mono-lux text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                per hour
              </span>
            </p>
          </div>
          <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{v.description}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {v.specs.map((s) => (
              <span
                key={s}
                className="font-mono-lux text-[10px] tracking-[0.15em] uppercase px-3 py-1 border border-[#2E3340] text-zinc-500"
              >
                {s}
              </span>
            ))}
          </div>
          <div className="mt-7 pt-6 border-t border-[#2E3340]/70 flex items-center justify-between">
            <div className="flex items-center gap-6 text-zinc-400">
              <span className="flex items-center gap-2 text-xs">
                <Users size={14} className="text-amber-500" /> {v.passengers} guests
              </span>
              <span className="flex items-center gap-2 text-xs">
                <Luggage size={14} className="text-amber-500" /> {v.luggage} cases
              </span>
            </div>
            <Link
              to={`/book?vehicle=${v.id}`}
              data-testid={`fleet-reserve-${v.id}`}
              className="flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.25em] text-amber-400 hover:text-amber-200 transition-colors"
            >
              Reserve <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export function Fleet() {
  const [vehicles, setVehicles] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/vehicles")
      .then((r) => setVehicles(r.data))
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => ["All", ...new Set(vehicles.map((v) => v.category))], [vehicles]);
  const filtered = active === "All" ? vehicles : vehicles.filter((v) => v.category === active);

  return (
    <section id="fleet" className="relative py-28 lg:py-36 bg-[#16181D]" data-testid="fleet-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>The Fleet</Overline>
            <h2 className="font-serif-lux font-light text-4xl sm:text-5xl lg:text-6xl text-amber-50 mt-4 leading-tight">
              Machines of <span className="gold-text italic">quiet power.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="fleet-category-tabs">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`fleet-tab-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActive(c)}
                className={`font-mono-lux text-[10px] uppercase tracking-[0.25em] px-4 py-2 border transition-colors duration-300 ${
                  active === c
                    ? "border-amber-500 text-amber-300 bg-amber-500/10"
                    : "border-[#2E3340] text-zinc-500 hover:border-amber-500/50 hover:text-zinc-300"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        {loading ? (
          <p className="mt-16 font-mono-lux text-xs uppercase tracking-[0.3em] text-zinc-600" data-testid="fleet-loading">
            Preparing the fleet…
          </p>
        ) : (
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="fleet-grid">
            {filtered.map((v, i) => (
              <VehicleCard key={v.id} v={v} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
